/**
 * Live Khutbah (an add-on): one screen listens to the imam (its microphone or a mixer feed), the Arabic is written
 * down as it's heard, translated into English, and shown word by word, full screen, on the chosen screens. Started
 * from the dashboard, the iPhone app or its widget, or by itself at Jumu'ah; it covers everything, salah blanking,
 * idle and switched-off screens included, until it's stopped, and the screens go back to what they were doing.
 *
 * The look is fixed (it has to work every time); only its colours can be changed.
 */

import type { Masjid, TimetableDay } from './types';
import { jumuahFor } from './prayer';
import { addDays, weekdayOf, ymd, zonedToInstant } from './time';
import { autoInk, hexToRgb, rgbToHex } from './look';

/** Background, and the underline colours: not heard clearly, a Qur'an ayah, a hadith. */
export interface KhutbahColours { background: string; unclear: string; quran: string; hadith: string }

export interface KhutbahSettings {
  /** The screen that listens (its microphone or a mixer feed). */
  listenerScreenId: string | null;
  /** Screens that show it; null is every screen. */
  screenIds: string[] | null;
  colours: KhutbahColours;
  /** Start by itself at each Jumu'ah khutbah (a few minutes before), and stop when the jama'ah starts. */
  auto: { enabled: boolean; before: number };
}

/** White on black, and black on white. Underline colours are Apple's, the darker ones on white so they still read. */
export const KHUTBAH_LOOKS: Array<{ id: 'dark' | 'light'; label: string; colours: KhutbahColours }> = [
  { id: 'dark', label: 'White on black', colours: { background: '#000000', unclear: '#FF6961', quran: '#30D158', hadith: '#BF5AF2' } },
  { id: 'light', label: 'Black on white', colours: { background: '#FFFFFF', unclear: '#D70015', quran: '#248A3D', hadith: '#8944AB' } },
];
export const KHUTBAH_SWATCHES = ['#000000', '#FFFFFF', '#0B1F17', '#0A2A66', '#1C1C1E', '#F5F1E8'] as const;
export const UNDERLINE_SWATCHES = ['#FF6961', '#D70015', '#FF9F0A', '#30D158', '#248A3D', '#0A84FF', '#BF5AF2', '#8944AB', '#FFD60A'] as const;

const HEX = /^#[0-9A-Fa-f]{6}$/;
const hex = (v: unknown, d: string) => (typeof v === 'string' && HEX.test(v) ? v.toUpperCase() : d);

export function defaultKhutbah(): KhutbahSettings {
  return { listenerScreenId: null, screenIds: null, colours: { ...KHUTBAH_LOOKS[0]!.colours }, auto: { enabled: false, before: 2 } };
}

/** Settings as saved (older, partial or missing) made whole. */
export function normalizeKhutbah(raw: unknown): KhutbahSettings {
  const d = defaultKhutbah();
  if (!raw || typeof raw !== 'object') return d;
  const r = raw as Partial<KhutbahSettings> & { colours?: Partial<KhutbahColours>; auto?: Partial<KhutbahSettings['auto']> };
  const c: Partial<KhutbahColours> = r.colours ?? {};
  return {
    listenerScreenId: typeof r.listenerScreenId === 'string' && r.listenerScreenId ? r.listenerScreenId : null,
    screenIds: Array.isArray(r.screenIds) ? r.screenIds.filter((x): x is string => typeof x === 'string') : null,
    colours: { background: hex(c.background, d.colours.background), unclear: hex(c.unclear, d.colours.unclear), quran: hex(c.quran, d.colours.quran), hadith: hex(c.hadith, d.colours.hadith) },
    auto: { enabled: r.auto?.enabled === true, before: Math.max(0, Math.min(15, Math.round(Number(r.auto?.before ?? d.auto.before)) || 0)) },
  };
}

/** The text colour that reads on the background (white or near black), and the Arabic's (a little softer). */
export function khutbahInk(background: string) {
  const ink = rgbToHex(autoInk(hexToRgb(background)));
  const light = ink.toUpperCase() === '#FFFFFF';
  return { ink, arabic: light ? 'rgba(255,255,255,.74)' : 'rgba(0,0,0,.64)', faint: light ? 'rgba(255,255,255,.62)' : 'rgba(0,0,0,.6)', light };
}

/* ---------- what was said ---------- */

/** p plain, q a Qur'an ayah, h a hadith, u not heard clearly (or the translation isn't sure). */
export type KhutbahKind = 'p' | 'q' | 'h' | 'u';
export interface KhutbahSegment { t: string; k: KhutbahKind }
export interface KhutbahLine {
  seq: number;
  arabic: KhutbahSegment[];
  /** Null until it's translated. */
  english: KhutbahSegment[] | null;
  /** Under the English: where a Qur'an ayah or hadith is from. */
  ref: { k: 'q' | 'h'; text: string } | null;
  /**
   * False while the sentence is still being spoken and translated (the English grows a few words at a time); the
   * screens only move on to the next sentence once it's done. Missing means done.
   */
  done?: boolean;
}

const KINDS = new Set(['p', 'q', 'h', 'u']);
export function segmentsOf(raw: unknown): KhutbahSegment[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((x) => (x && typeof x === 'object' && typeof (x as KhutbahSegment).t === 'string' && (x as KhutbahSegment).t.trim()
    ? [{ t: (x as KhutbahSegment).t, k: (KINDS.has((x as KhutbahSegment).k) ? (x as KhutbahSegment).k : 'p') as KhutbahKind }] : []));
}
export function lineOf(r: { seq: unknown; arabic: unknown; english: unknown; ref: unknown; done?: unknown }): KhutbahLine {
  const ref = r.ref && typeof r.ref === 'object' && typeof (r.ref as { text?: unknown }).text === 'string' ? r.ref as { k: string; text: string } : null;
  return { seq: Number(r.seq) || 0, arabic: segmentsOf(r.arabic), english: r.english == null ? null : segmentsOf(r.english), ref: ref ? { k: ref.k === 'h' ? 'h' : 'q', text: ref.text.slice(0, 160) } : null, done: r.done !== false };
}

/** The first `n` words of a line's parts (the sample khutbah's English, arriving a few words at a time). */
export function firstWords(segs: readonly KhutbahSegment[], n: number): KhutbahSegment[] {
  const out: KhutbahSegment[] = [];
  let left = n;
  for (const s of segs) {
    if (left <= 0) break;
    const parts = s.t.split(/\s+/).filter(Boolean);
    out.push({ t: parts.slice(0, left).join(' '), k: s.k });
    left -= parts.length;
  }
  return out.filter((s) => s.t);
}

/** Words of a line, each keeping its kind, so they can appear one at a time with their underline. */
export function wordsOf(segs: readonly KhutbahSegment[]): Array<{ t: string; k: KhutbahKind; gap: boolean }> {
  const out: Array<{ t: string; k: KhutbahKind; gap: boolean }> = [];
  segs.forEach((s, si) => {
    const parts = s.t.split(/\s+/).filter(Boolean);
    parts.forEach((t, i) => out.push({ t, k: s.k, gap: i < parts.length - 1 ? true : si < segs.length - 1 }));
  });
  return out;
}

/** The sample shown in previews (the dashboard's sheet, the app, the Studio of the add-on) and the demo. */
export const KHUTBAH_SAMPLE: KhutbahLine[] = [
  { seq: 1, arabic: [{ t: 'الحمد لله، نحمده ونستعينه.', k: 'p' }], english: [{ t: 'All praise is due to Allah; we praise Him and we seek His help.', k: 'p' }], ref: null },
  { seq: 2, arabic: [{ t: 'أيها الإخوة والأخوات، أريد أن أتحدث اليوم عن الصبر.', k: 'p' }], english: [{ t: 'Dear brothers and sisters, today I want to speak about patience.', k: 'p' }], ref: null },
  { seq: 3, arabic: [{ t: 'يقول الله تعالى:', k: 'p' }, { t: 'يٰۤاَيُّهَا الَّذِيْنَ اٰمَنُوا اسْتَعِيْنُوْا بِالصَّبْرِ وَالصَّلٰوةِ ؕ اِنَّ اللّٰهَ مَعَ الصّٰبِرِيْنَ', k: 'q' }],
    english: [{ t: 'Allah says:', k: 'p' }, { t: '“O you who have believed, seek help through patience and prayer. Indeed, Allah is with the patient.”', k: 'q' }], ref: { k: 'q', text: 'Qur’an, Al-Baqarah 2:153 · Sahih International' } },
  { seq: 4, arabic: [{ t: 'نبي الله أيوب عليه السلام ابتُلي', k: 'p' }, { t: 'سنين طويلة', k: 'u' }, { t: 'في صحته وأهله.', k: 'p' }],
    english: [{ t: 'Prophet Ayyub, peace be upon him, was tested for', k: 'p' }, { t: 'many years', k: 'u' }, { t: 'in his health and in his family.', k: 'p' }], ref: null },
  { seq: 5, arabic: [{ t: 'قال رسول الله ﷺ:', k: 'p' }, { t: '«وَمَنْ يَتَصَبَّرْ يُصَبِّرْهُ اللَّهُ، وَمَا أُعْطِيَ أَحَدٌ عَطَاءً خَيْرًا وَأَوْسَعَ مِنَ الصَّبْرِ»', k: 'h' }],
    english: [{ t: 'The Prophet ﷺ said:', k: 'p' }, { t: '“Whoever remains patient, Allah will make him patient. Nobody can be given a blessing better and greater than patience.”', k: 'h' }], ref: { k: 'h', text: 'Hadith, Sahih al-Bukhari 1469' } },
  { seq: 6, arabic: [{ t: 'فلنكن من أهل الصبر، ومن أهل الصلاة.', k: 'p' }], english: [{ t: 'So let us be people of patience, and people of prayer.', k: 'p' }], ref: null },
];

/* ---------- by itself at Jumu'ah ---------- */

/**
 * The Jumu'ah khutbah to start now, if it starts by itself: from `before` minutes before a khutbah's time until 10
 * minutes after it, ending when that Jumu'ah's jama'ah starts (plus a couple of minutes). Uses the timetable's
 * Jumu'ah times when it has them, as the screens do.
 */
export function autoKhutbahNow(masjid: Masjid, timetable: ReadonlyMap<string, TimetableDay> | undefined, settings: KhutbahSettings, now: Date): { slot: Date; ends: Date } | null {
  if (!settings.auto.enabled) return null;
  const tz = masjid.timezone;
  for (const day of [ymd(now, tz), addDays(ymd(now, tz), -1)]) {
    if (weekdayOf(day) !== 5) continue;
    for (const s of jumuahFor(masjid, timetable?.get(day))) {
      if (!s.khutbah || !s.jamaah) continue;
      const slot = zonedToInstant(day, s.khutbah, tz), jamaah = zonedToInstant(day, s.jamaah, tz);
      const t = now.getTime();
      if (t >= slot.getTime() - settings.auto.before * 60_000 && t < Math.min(slot.getTime() + 10 * 60_000, jamaah.getTime())) {
        return { slot, ends: new Date(Math.max(jamaah.getTime() + 2 * 60_000, t + 5 * 60_000)) };
      }
    }
  }
  return null;
}

/** Whether a masjid can use Live Khutbah (the add-on). */
export const hasKhutbah = (m: Pick<Masjid, 'khutbahAddon'>) => m.khutbahAddon === true;

export const KHUTBAH_ADDON = {
  name: 'Live Khutbah',
  price: '£10',
  note: 'a month, on top of any plan',
  feats: ['The khutbah translated into English, live, on your screens', 'Arabic above, English below, word by word', 'Qur’an ayahs and hadith marked, with their source', 'Starts from the dashboard, the app, its widget, or by itself at Jumu‘ah'],
} as const;
